/**
 * Another racer's craft in an online race (ACT HQ t-492): a person on another device, or a
 * bot that device runs. It's drawn from the states the room passes on, a little behind
 * (INTERP_MS) so there are two to blend between; if they stop coming, it runs on along its
 * last heading for a moment, then waits.
 */
import { Quaternion, Vector3 } from "@babylonjs/core/Maths/math.vector";
import type { Scene } from "@babylonjs/core/scene";
import { VD_STATE_FLAG, type VdCraftState } from "@act/shared";
import { Ship } from "../ship/Ship";
import type { ShipSpec } from "../config/ships";

/** How far behind the newest state the craft is drawn (ms): about two messages. */
const INTERP_MS = 110;
/** How long it runs on past the last state before it waits (ms). */
const EXTRAPOLATE_MS = 250;
/** States kept (a second's worth at 20 a second). */
const KEEP = 20;

type Timed = { at: number; s: VdCraftState };

export class RemoteCraft {
  readonly ship: Ship;
  private readonly states: Timed[] = [];
  private readonly pos = new Vector3();
  private readonly rot = new Quaternion();
  private readonly qa = new Quaternion();
  private readonly qb = new Quaternion();

  constructor(scene: Scene, spec: ShipSpec) {
    this.ship = new Ship(scene, spec);
  }

  /** Its start, before any state arrives. */
  placeAt(position: Vector3, forward: Vector3): void {
    this.ship.placeAtStart(position, forward);
  }

  /** A state from the room, with this device's time it arrived (performance.now()). */
  push(s: VdCraftState, at: number): void {
    const last = this.states[this.states.length - 1];
    if (last && at < last.at) return;
    this.states.push({ at, s });
    if (this.states.length > KEEP) this.states.shift();
  }

  /** The newest state (the hand-over starts the bots from it). */
  get latest(): VdCraftState | null {
    return this.states[this.states.length - 1]?.s ?? null;
  }

  /** Progress round the lap from the newest state (0..1). */
  get trackT(): number {
    return this.latest?.trackT ?? 0;
  }

  update(now: number, dt: number): void {
    const n = this.states.length;
    if (n === 0) return;
    const at = now - INTERP_MS;
    let a = this.states[0]!;
    let b = a;
    for (let i = 1; i < n; i++) {
      b = this.states[i]!;
      if (b.at >= at) break;
      a = b;
    }
    let k = 0;
    if (b.at > a.at) k = Math.max(0, Math.min(1, (at - a.at) / (b.at - a.at)));
    if (at > b.at && n >= 2) {
      // Past the newest: run on from the last two, for a little while.
      const prev = this.states[n - 2]!;
      const span = b.at - prev.at;
      a = prev;
      k = span > 0 ? 1 + Math.min(at - b.at, EXTRAPOLATE_MS) / span : 1;
    }
    // A respawn jumps: never blend across one.
    if (b.s.flags & VD_STATE_FLAG.respawned) {
      a = b;
      k = 0;
    }
    this.pos.set(a.s.x + (b.s.x - a.s.x) * k, a.s.y + (b.s.y - a.s.y) * k, a.s.z + (b.s.z - a.s.z) * k);
    this.qa.set(a.s.qx, a.s.qy, a.s.qz, a.s.qw);
    this.qb.set(b.s.qx, b.s.qy, b.s.qz, b.s.qw);
    Quaternion.SlerpToRef(this.qa, this.qb, Math.min(1, k), this.rot);
    const speed = a.s.speed + (b.s.speed - a.s.speed) * Math.min(1, k);
    this.ship.showAt(this.pos, this.rot, speed, (b.s.flags & VD_STATE_FLAG.boosting) !== 0, dt);
  }

  dispose(): void {
    this.ship.root.dispose();
  }
}
